import { FC, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { MuiModal } from "../../../../../../components";
import { useSiteCore } from "../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";

import { useGetClientActivityById } from "../../../../../../api/services/client/clientApis";
import { InformationBadge } from "../../../../../../common/informationBadge/informationBadge";
import { ViewManageLocale } from "../../../../../../components/manageLocale/ViewManageLocale";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../helpers/utils";
import { useToast } from "../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../redux/slices/appState/appStateSlice";
import { CreateClientActivitiesRowValue } from "../../modelsAndSchemas/models";
import { ViewActivityWrapper } from "./ViewActivity.style";
import { ViewActivityDefault } from "./viewActivityDefault/ViewActivityDefault";
import { ViewActivityGeneral } from "./viewActivityGeneral/ViewActivityGeneral";

interface CreateActivityProps {
  show: boolean;
  handleCloseModal: () => void;
  selectedActivity: CreateClientActivitiesRowValue;
  isView?: boolean;
  editButtonClick?: () => void;
}

export const ViewActivity: FC<CreateActivityProps> = ({
  show,
  handleCloseModal,
  selectedActivity,
  isView,
  editButtonClick,
}) => {
  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const { commonLabels, fetchLabel } = useSiteCore();
  const [showManageLocale, setShowManageLocale] = useState(false);

  const handleShowManageLocale = () => {
    setShowManageLocale(true);
  };

  const handleCloseShowManageLocale = () => {
    setShowManageLocale(false);
  };
  const {
    data: activityData,
    isFetching,
    isError: isActivityDataError,
  } = useGetClientActivityById(selectedActivity.id);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible: isFetching,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [isFetching]);

  useEffect(() => {
    if (isActivityDataError) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [isActivityDataError]);

  const title = useMemo(
    () =>
      replaceHashWithValue(
        fetchLabel(
          activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
          activitiesAndDeliverablesSiteCoreKeys.viewActivityPopupTitle,
        ),
        getValueOrBlank(activityData?.activityType?.name),
      ),
    [activityData?.activityType?.name],
  );

  const bannerInfoMessage = fetchLabel(
    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
    activitiesAndDeliverablesSiteCoreKeys.editActivityNote,
  );

  return (
    <>
      <MuiModal
        open={show}
        onClose={handleCloseModal}
        title={title}
        primaryButtonTitle=""
        secondaryButtonTitle={commonLabels.close}
        onSecondaryButtonClick={handleCloseModal}
        tertiaryButtonTitle={
          !isView
            ? fetchLabel(
                activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                activitiesAndDeliverablesSiteCoreKeys.editActivityButton,
              )
            : ""
        }
        onTertiaryButtonClick={editButtonClick}
        hasLargeContent
        stackButtonsOnMobile={true}
      >
        <ViewActivityWrapper>
          <InformationBadge message={bannerInfoMessage} />
          <ViewActivityGeneral
            onViewManageLocal={handleShowManageLocale}
            activityData={activityData}
          >
            <ViewActivityDefault activityData={activityData} />
          </ViewActivityGeneral>
        </ViewActivityWrapper>
      </MuiModal>
      {showManageLocale && (
        <ViewManageLocale
          show={showManageLocale}
          handleCloseModal={handleCloseShowManageLocale}
          localeLanguages={activityData?.localizedTopicNames ?? []}
          title={title}
          bannerInfoMessage={bannerInfoMessage}
        />
      )}
    </>
  );
};
